import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ArchiveBoxIcon,
    CheckIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import CatalogStatusChip, {
    catalogStatusLabel,
    normalizeCatalogStatus,
} from "../productCatalog/CatalogStatusChip";
import { ProductCatalogSelectFilter } from "../productCatalog/ProductCatalogSelectFilter";
import SupplySku from "../SupplySku";
import Drawer from "../Drawer";
import { ConfrimModal } from "../ConfrimModal";
import { LoaderSpinner } from "../LoaderSpinner";
import {
    BomUiLoadingKeys,
    clearBomFilters,
    clearBomRowSelection,
    createSupplierCategory,
    deleteBomSupply,
    deleteSupplierCategory,
    fetchSuppliesBomUi,
    selectBomTabSelection,
    selectBomUi,
    setBomFilter,
    setBomRowSelection,
    toggleBomRowSelection,
    searchBomProducts,
    upsertBomSupply,
} from "../../slices/bomUiSlice";
import { ProductGroupLoadingKeys, getSuppliers } from "../../slices/productGroupSlice";
import { useDebounce } from "../../helpers/useDebounce";
import SuppliesBulkEditBar from "./SuppliesBulkEditBar";

const amberWhenFilterActive = (isActive) =>
    isActive ? "!border-amber-400 !text-amber-700 !bg-amber-50 focus:!ring-amber-400" : "";

const SUPPLY_DRAWER_STACK = {
    status: "supply-st",
    category: "supply-cat",
    supplier: "supply-sup",
};

const SUPPLY_STATUS_SELECT_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "PLANNED", label: "Planned" },
    { value: "SUNSET", label: "Sunset" },
    { value: "IN_ACTIVE", label: "Inactive" },
];

function getSupplyRowId(row) {
    return String(row?.product_id ?? row?.id ?? "").trim();
}

function buildSupplyUpsertBody(row, patch = {}) {
    const product_id = getSupplyRowId(row);
    const notes = row.notes && row.notes !== "—" ? String(row.notes).trim() : null;

    const supplier_category_id =
        patch.supplier_category_id !== undefined
            ? patch.supplier_category_id
            : row.supplier_category_id != null
              ? Number(row.supplier_category_id)
              : null;

    const vendor_id =
        patch.vendor_id !== undefined ? patch.vendor_id : row.vendor_id != null ? String(row.vendor_id) : null;

    const status = patch.status !== undefined ? patch.status : row.status_code || "ACTIVE";

    const unit_cost =
        patch.unit_cost !== undefined ? patch.unit_cost : (row.unitCost != null ? Number(row.unitCost) : null);

    return {
        product_id,
        supplier_category_id,
        vendor_id,
        status,
        unit_cost,
        notes,
    };
}

function emptySupplyDraft() {
    return {
        id: "",
        productId: "",
        sku: "",
        name: "",
        categoryId: "",
        vendorId: "",
        supplier: "",
        status: "ACTIVE",
        unitCost: "",
        barcode: "",
        notes: "",
        reusableEquipment: false,
    };
}

function SupplyStatusBadge({ status, statusCode }) {
    const normalizedStatus =
        normalizeCatalogStatus(statusCode) || normalizeCatalogStatus(status);
    return normalizedStatus ? (
        <CatalogStatusChip status={normalizedStatus} label={catalogStatusLabel(normalizedStatus)} />
    ) : (
        "—"
    );
}

function SupplyDrawer({
    isOpen,
    onClose,
    initialValue,
    onSave,
    supplyCategories,
    onCreateCategory,
    onDeleteCategory,
    categoryMutationBusy,
    vendorOptions,
    vendorSelectLoading,
    isSaving,
}) {
    const dispatch = useDispatch();
    const isEdit = Boolean(initialValue);
    const [draft, setDraft] = useState(emptySupplyDraft());
    const [childSearch, setChildSearch] = useState("");
    const [childMatches, setChildMatches] = useState([]);
    const [childDropOpen, setChildDropOpen] = useState(false);
    const [searchingChild, setSearchingChild] = useState(false);
    const childRef = useRef(null);
    const [openDrawerSelectKey, setOpenDrawerSelectKey] = useState(null);
    const debouncedChildSearch = useDebounce(childSearch, 250);

    const [editingCategories, setEditingCategories] = useState(false);
    const [newCategoryInput, setNewCategoryInput] = useState("");

    const categoryOptions = useMemo(
        () =>
            (supplyCategories || []).map((c) => ({
                value: String(c.id),
                label: c.name,
            })),
        [supplyCategories]
    );

    useEffect(() => {
        if (!isOpen) {
            setEditingCategories(false);
            setNewCategoryInput("");
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || isEdit || !debouncedChildSearch.trim()) {
            setChildMatches([]);
            setSearchingChild(false);
            return undefined;
        }
        let active = true;
        setSearchingChild(true);
        dispatch(
            searchBomProducts({
                search: debouncedChildSearch.trim(),
                limit: 8,
                excludeExistingProductBom: false,
                excludeExistingSupplyProduct: true,
            })
        )
            .unwrap()
            .then((data) => {
                if (active) setChildMatches(data?.items || []);
            })
            .catch(() => {
                if (active) setChildMatches([]);
            })
            .finally(() => {
                if (active) setSearchingChild(false);
            });
        return () => {
            active = false;
        };
    }, [debouncedChildSearch, dispatch, isEdit, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setOpenDrawerSelectKey(null);
            return;
        }
        const base = emptySupplyDraft();
        if (initialValue) {
            setDraft({
                ...base,
                ...base,
                id: initialValue.id || initialValue.product_id || "",
                productId: initialValue.product_id || initialValue.id || "",
                sku: initialValue.sku || "",
                name: initialValue.canonicalName || initialValue.name || "",
                categoryId: initialValue.supply_category_id || "",
                vendorId: initialValue.vendor_id || "",
                supplier: initialValue.supplier || "",
                status: initialValue.status_code || "ACTIVE",
                unitCost: initialValue.unitCost != null ? String(initialValue.unitCost) : "",
                barcode: initialValue.barcode || "",
                notes: initialValue.notes && initialValue.notes !== "-" ? initialValue.notes : "",
                reusableEquipment:
                    initialValue.reusable_equipment === true || initialValue.reusable_equipment === 1,
            });
            setChildSearch(initialValue.sku || "");
        } else {
            setDraft(base);
            setChildSearch("");
        }
        setChildDropOpen(false);
        setChildMatches([]);
    }, [isOpen, initialValue]);

    const handleSave = () => {
        const productId = String(draft.productId || "").trim();
        if (!productId) {
            toast.error("SKU is required", { position: "bottom-right" });
            return;
        }
        onSave({
            product_id: productId,
            supplier_category_id: draft.categoryId ? Number(draft.categoryId) : null,
            vendor_id: draft.vendorId || null,
            status: draft.status || "ACTIVE",
            unit_cost: draft.unitCost === "" ? null : Number(draft.unitCost || 0),
            notes: String(draft.notes || "").trim() || null,
            reusable_equipment: draft.reusableEquipment === true,
        });
    };

    const selectChildRow = (row) => {
        setDraft((prev) => ({
            ...prev,
            productId: row.id,
            sku: row.sku || "",
            name: row.canonicalName || row.name || "",
            vendorId: row.vendor_id ? String(row.vendor_id) : "",
            supplier: row.supplier || "",
            status: row.status_code || "ACTIVE",
            barcode: row.barcode || "",
        }));
        setChildSearch(row.sku || "");
        setChildDropOpen(false);
    };

    const handleAddCategory = async () => {
        const trimmed = newCategoryInput.trim();
        if (!trimmed) return;
        try {
            const created = await onCreateCategory(trimmed);
            if (created?.id != null) {
                setDraft((prev) => ({ ...prev, categoryId: String(created.id) }));
            }
            setNewCategoryInput("");
            toast.success("Category added", { position: "bottom-right" });
        } catch (error) {
            const message = typeof error === "string" ? error : error?.message || "Failed to add category";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const handleRemoveCategory = async (category) => {
        const id = category?.id;
        if (id == null) return;
        try {
            await onDeleteCategory(id);
            setDraft((prev) =>
                String(prev.categoryId) === String(id) ? { ...prev, categoryId: "" } : prev
            );
            toast.success("Category removed", { position: "bottom-right" });
        } catch (error) {
            const message = typeof error === "string" ? error : error?.message || "Failed to remove category";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const footer = (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-8 items-center rounded-md border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`inline-flex relative h-8 items-center rounded-md bg-[#111] px-3 text-xs font-medium text-white transition hover:bg-[#333] ${isSaving ? "opacity-50" : ""}`}
                >
                    {isSaving && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                            <LoaderSpinner width="14" height="14" colorSpinner="white" border="2" />
                        </div>
                    )}
                    {isEdit ? "Save Changes" : "Add Supply"}
                </button>
            </div>
        </div>
    );

    const inputClass =
        "h-8 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400";
    const labelClass = "mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500";

    return (
        <Drawer isOpen={isOpen} onClose={onClose} width="480px" footer={footer}>
            <div className="flex h-full flex-col overflow-hidden">
                <div className="border-b border-gray-200 px-6 py-4">
                    <div className="mb-1 flex items-center gap-2 text-xs text-gray-400">
                        <span>BOM</span>
                        <span>/</span>
                        <span>Supplies</span>
                    </div>
                    <h2 className="text-base font-semibold text-gray-900">
                        {isEdit ? `Edit Supply — ${initialValue?.sku || draft.sku}` : "Add Supply Item"}
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">
                        {isEdit ? "Update supply item details." : "Add a new packaging supply to the catalog."}
                    </p>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
                    <div className="grid grid-cols-2 gap-3 content-center">
                        <div className="flex flex-col gap-1">
                            <span className={labelClass}>SKU *</span>
                            {isEdit ? (
                                <input
                                    value={draft.sku}
                                    disabled
                                    className={`${inputClass} font-mono opacity-80 h-[35px]`}
                                />
                            ) : (
                                <div className="relative" ref={childRef}>
                                    <input
                                        value={childSearch}
                                        onChange={(e) => {
                                            const v = e.target.value.toUpperCase();
                                            setChildSearch(v);
                                            setDraft((prev) => ({
                                                ...prev,
                                                sku: v,
                                                productId: "",
                                                name: "",
                                                barcode: "",
                                            }));
                                            setChildDropOpen(v.length > 0);
                                        }}
                                        onFocus={() => childSearch.length > 0 && setChildDropOpen(true)}
                                        onBlur={() => setTimeout(() => setChildDropOpen(false), 150)}
                                        placeholder="Search or type SKU…"
                                        className={`${inputClass} font-mono h-[35px]`}
                                    />
                                    {childDropOpen && (childMatches.length > 0 || searchingChild) ? (
                                        <div className="absolute left-0 right-0 top-full z-50 mt-0.5 max-h-48 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
                                            {searchingChild ? (
                                                <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>
                                            ) : null}
                                            {childMatches.map((row) => (
                                                <button
                                                    key={row.id || row.sku}
                                                    type="button"
                                                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => selectChildRow(row)}
                                                >
                                                    <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
                                                        {row.sku}
                                                    </span>
                                                    <span className="truncate text-xs text-gray-400">{row.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className={labelClass}>Status</span>
                            <AddDrawerSelect
                                options={SUPPLY_STATUS_SELECT_OPTIONS}
                                value={draft.status}
                                onChange={(v) => setDraft((prev) => ({ ...prev, status: v || "ACTIVE" }))}
                                noneLabel="Select status…"
                                emptyLabel="Select status…"
                                buttonSelectedClassname="!py-1.5"
                                disabled={!isEdit}
                                selectClassname="w-full"
                                stackKey={SUPPLY_DRAWER_STACK.status}
                                openStackKey={openDrawerSelectKey}
                                onStackKeyChange={setOpenDrawerSelectKey}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <span className={labelClass}>Name *</span>
                        <input
                            value={draft.name}
                            placeholder="e.g. Branded Box 12 x 8 x 6"
                            className={`${inputClass} h-[35px]`}
                            readOnly
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <span className={labelClass}>Category</span>
                                <button
                                    type="button"
                                    onClick={() => setEditingCategories((v) => !v)}
                                    className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-700"
                                    title="Edit categories"
                                    disabled={categoryMutationBusy}
                                >
                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            {editingCategories ? (
                                <div className="mt-1 space-y-1.5 rounded border border-gray-200 bg-gray-50 p-2">
                                    {(supplyCategories || []).map((cat) => (
                                        <div key={cat.id} className="flex items-center justify-between gap-1">
                                            <span className="text-sm text-gray-700">{cat.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCategory(cat)}
                                                disabled={categoryMutationBusy}
                                                className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                                                title="Remove category"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                    <div className="flex gap-1 pt-1">
                                        <input
                                            value={newCategoryInput}
                                            onChange={(e) => setNewCategoryInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddCategory();
                                                }
                                            }}
                                            placeholder="New category…"
                                            disabled={categoryMutationBusy}
                                            className="h-7 min-w-0 flex-1 rounded border border-gray-200 bg-white px-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await handleAddCategory();
                                                setEditingCategories(false);
                                            }}
                                            disabled={categoryMutationBusy}
                                            className="inline-flex shrink-0 items-center justify-center rounded bg-[#111] px-2 py-1 text-white hover:bg-[#333] disabled:opacity-50"
                                            title="Save"
                                        >
                                            <CheckIcon className="h-3 w-3" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <AddDrawerSelect
                                    options={categoryOptions}
                                    value={draft.categoryId}
                                    onChange={(v) => setDraft((prev) => ({ ...prev, categoryId: v || "" }))}
                                    noneLabel="Select category…"
                                    emptyLabel="Select category…"
                                    buttonSelectedClassname="!py-1.5"
                                    selectClassname="w-full"
                                    stackKey={SUPPLY_DRAWER_STACK.category}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                    isLoading={categoryMutationBusy}
                                />
                            )}
                        </div>
                        <div className="space-y-1">
                            <span className={labelClass}>Unit Cost ($)</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={draft.unitCost}
                                onChange={(e) => setDraft((prev) => ({ ...prev, unitCost: e.target.value }))}
                                placeholder="0.00"
                                className={`${inputClass} h-[35px]`}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="flex cursor-pointer items-start gap-2">
                            <input
                                type="checkbox"
                                aria-label="Reusable warehouse equipment"
                                checked={draft.reusableEquipment}
                                onChange={(e) =>
                                    setDraft((prev) => ({ ...prev, reusableEquipment: e.target.checked }))
                                }
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                            />
                            <span>
                                <span className="block text-sm font-medium text-gray-900">
                                    Reusable warehouse equipment
                                </span>
                                <span className="block text-xs text-gray-400">
                                    Reusable containers/equipment (e.g. spare bins). Excluded from replenishment
                                    and Deep Reserve suggestions. Not sold.
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="space-y-1">
                        <span className={labelClass}>Supplier</span>
                        <AddDrawerSelect
                            options={vendorOptions}
                            value={draft.vendorId}
                            onChange={(v) => setDraft((prev) => ({ ...prev, vendorId: v || "" }))}
                            noneLabel="Select supplier…"
                            emptyLabel="Select supplier…"
                            buttonSelectedClassname="!py-1.5"
                            disabled={!isEdit}
                            selectClassname="w-full"
                            stackKey={SUPPLY_DRAWER_STACK.supplier}
                            openStackKey={openDrawerSelectKey}
                            onStackKeyChange={setOpenDrawerSelectKey}
                            isLoading={vendorSelectLoading}
                        />
                    </div>

                    <div className="space-y-1">
                        <span className={labelClass}>Barcode</span>
                        <input
                            value={draft.barcode}
                            placeholder="Auto-fills when SKU is selected"
                            className={`${inputClass} font-mono h-[35px]`}
                            readOnly
                        />
                    </div>

                    <div className="space-y-1">
                        <span className={labelClass}>Notes</span>
                        <input
                            value={draft.notes}
                            onChange={(e) => setDraft((prev) => ({ ...prev, notes: e.target.value }))}
                            placeholder="Optional notes"
                            className={`${inputClass} h-[35px]`}
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

const SuppliesBomPanel = ({ onMetricsChange }) => {
    const dispatch = useDispatch();
    const { filtersByTab, data, loadingKeys } = useSelector(selectBomUi);
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((s) => s.productGroup);
    const selectedIds = useSelector((s) => selectBomTabSelection(s, "supplies"));
    const filters = filtersByTab.supplies;
    const items = data.supplies || [];
    const supplyCategories = data.supplyCategories || [];
    const [editingItem, setEditingItem] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [bulkConfirm, setBulkConfirm] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);
    const headerCheckboxRef = useRef(null);

    useEffect(() => {
        dispatch(getSuppliers({}));
    }, [dispatch]);

    const vendorOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((vendor) => ({
                value: String(vendor.value),
                label: vendor.label,
            })),
        [vendorsToSelect]
    );

    const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const categoryMutationBusy =
        loadingKeys.includes(BomUiLoadingKeys.createSupplierCategory) ||
        loadingKeys.includes(BomUiLoadingKeys.deleteSupplierCategory);
    const isSavingSupply =
        loadingKeys.includes(BomUiLoadingKeys.upsertSupply) ||
        loadingKeys.includes(BomUiLoadingKeys.deleteSupply);

    const filteredRows = useMemo(() => {
        const search = String(filters.search || "").trim().toLowerCase();
        return items.filter((item) => {
            const canonicalName = item.canonicalName || item.name;
            const matchesSearch =
                !search ||
                [item.sku, canonicalName, item.category, item.supplier, item.notes]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(search);
            const matchesProductName = filters.productName === "all" || canonicalName === filters.productName;
            return matchesSearch && matchesProductName;
        });
    }, [filters, items]);

    const selectedRows = useMemo(
        () => filteredRows.filter((r) => selectedIds.includes(getSupplyRowId(r))),
        [filteredRows, selectedIds]
    );

    const allFilteredSelected =
        filteredRows.length > 0 && filteredRows.every((r) => selectedIds.includes(getSupplyRowId(r)));
    const someFilteredSelected = filteredRows.some((r) => selectedIds.includes(getSupplyRowId(r)));

    useEffect(() => {
        const el = headerCheckboxRef.current;
        if (el) {
            el.indeterminate = someFilteredSelected && !allFilteredSelected;
        }
    }, [someFilteredSelected, allFilteredSelected]);

    const toggleSelectAllFiltered = () => {
        const ids = filteredRows.map(getSupplyRowId);
        if (allFilteredSelected) {
            const next = selectedIds.filter((id) => !ids.includes(id));
            dispatch(setBomRowSelection({ tab: "supplies", ids: next }));
        } else {
            dispatch(setBomRowSelection({ tab: "supplies", ids: Array.from(new Set([...selectedIds, ...ids])) }));
        }
    };

    const productOptions = useMemo(
        () =>
            Array.from(new Set(items.map((item) => item.canonicalName || item.name).filter(Boolean)))
                .sort((a, b) => a.localeCompare(b))
                .map((value) => ({
                    value,
                    label: value,
                })),
        [items]
    );

    const hasFilters = Boolean(filters.search) || filters.productName !== "all";

    const productNameFilterValue = filters.productName || "all";
    const productNameSelected =
        productNameFilterValue === "all" || productNameFilterValue === "" ? [] : [String(productNameFilterValue)];

    useEffect(() => {
        onMetricsChange?.({
            totalRows: filteredRows.length,
            selectedRows: selectedRows.length,
            totalCost: filteredRows.reduce((sum, row) => sum + Number(row.unitCost || 0), 0),
        });
    }, [filteredRows, selectedRows.length, onMetricsChange]);

    const openAdd = () => {
        setEditingItem(null);
        setDrawerOpen(true);
    };

    const openEdit = (row) => {
        setEditingItem(row);
        setDrawerOpen(true);
    };

    const handleDelete = (row, e) => {
        e.stopPropagation();
        setDeleteTarget(row);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await dispatch(deleteBomSupply({ productId: deleteTarget.product_id || deleteTarget.id })).unwrap();
            await dispatch(fetchSuppliesBomUi()).unwrap();
            dispatch(clearBomRowSelection("supplies"));
            toast.success(`${deleteTarget.sku} removed from supplies`, { position: "bottom-right" });
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
        } catch (error) {
            const message = typeof error === "string" ? error : error?.message || "Failed to delete supply";
            toast.error(message, { position: "bottom-right" });
        }
    };

    const handleBulkApplyRequest = (detail) => {
        if (!selectedRows.length) return;
        setBulkConfirm({ ...detail, rows: selectedRows });
    };

    const bulkConfirmTitle = () => {
        if (!bulkConfirm?.rows?.length) return "";
        const n = bulkConfirm.rows.length;
        switch (bulkConfirm.kind) {
            case "category":
                return `Set category to "${bulkConfirm.categoryLabel}" for ${n} supply item${n === 1 ? "" : "s"}?`;
            case "supplier":
                return `Set supplier to "${bulkConfirm.supplierLabel}" for ${n} supply item${n === 1 ? "" : "s"}?`;
            case "cost":
                return `Set unit cost to $${Number(bulkConfirm.unitCost).toFixed(2)} for ${n} supply item${
                    n === 1 ? "" : "s"
                }?`;
            case "status":
                return `Set product status to "${bulkConfirm.statusLabel}" for ${n} supply item${
                    n === 1 ? "" : "s"
                }?`;
            case "delete":
                return `Remove ${n} supply item${n === 1 ? "" : "s"} from the catalog?`;
            default:
                return "Apply bulk action?";
        }
    };

    const confirmBulkApply = async () => {
        if (!bulkConfirm?.rows?.length) return;
        setBulkRunning(true);
        try {
            const { kind, rows } = bulkConfirm;
            if (kind === "delete") {
                for (const row of rows) {
                    await dispatch(deleteBomSupply({ productId: getSupplyRowId(row) })).unwrap();
                }
                await dispatch(fetchSuppliesBomUi()).unwrap();
                toast.success(`${rows.length} supply item(s) removed`, { position: "bottom-right" });
            } else {
                let patch = {};
                if (kind === "category") {
                    patch = { supplier_category_id: bulkConfirm.packagingCategoryId };
                } else if (kind === "supplier") {
                    patch = { vendor_id: bulkConfirm.vendorId };
                } else if (kind === "cost") {
                    patch = { unit_cost: bulkConfirm.unitCost };
                } else if (kind === "status") {
                    patch = { status: bulkConfirm.status };
                }
                for (const row of rows) {
                    const body = buildSupplyUpsertBody(row, patch);
                    await dispatch(upsertBomSupply(body)).unwrap();
                }
                await dispatch(fetchSuppliesBomUi()).unwrap();
                toast.success(`Updated ${rows.length} supply item(s)`, { position: "bottom-right" });
            }
            dispatch(clearBomRowSelection("supplies"));
            setBulkConfirm(null);
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : error?.message || "Bulk action failed";
            toast.error(message, { position: "bottom-right" });
        } finally {
            setBulkRunning(false);
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            {/* Toolbar — matches SuppliesTab */}
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-2.5">
                <div className="relative max-w-sm flex-1">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(e) => dispatch(setBomFilter({ tab: "supplies", key: "search", value: e.target.value }))}
                        placeholder="Search SKU or name…"
                        className="h-[35px] w-full rounded-md no-search-input-chrome border border-gray-200 bg-white pl-8 pr-8 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-gray-400"
                    />
                    {filters.search ? (
                        <button
                            type="button"
                            onClick={() => dispatch(setBomFilter({ tab: "supplies", key: "search", value: "" }))}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                        >
                            <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
                <ProductCatalogSelectFilter
                    allLabel="All Products"
                    selectClassname="w-[180px] flex-shrink-0"
                    buttonSelectedClassname={amberWhenFilterActive(productNameSelected.length > 0)}
                    options={productOptions}
                    selectedOptions={productNameSelected}
                    onChange={(next) =>
                        dispatch(
                            setBomFilter({
                                tab: "supplies",
                                key: "productName",
                                value: next.length === 0 ? "all" : next[0],
                            })
                        )
                    }
                    renderOption={(option) => <span className="text-gray-900">{option.label}</span>}
                />
                {hasFilters ? (
                    <button
                        type="button"
                        onClick={() => dispatch(clearBomFilters("supplies"))}
                        className="flex h-8 items-center gap-1 px-2 text-sm text-gray-500 hover:text-gray-900"
                    >
                        <XMarkIcon className="h-3 w-3" />
                        Clear
                    </button>
                ) : null}
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex h-8 items-center gap-1 rounded-md bg-[#111] px-3 text-sm font-medium text-white hover:bg-[#333]"
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add Supply
                </button>
            </div>

            <SuppliesBulkEditBar
                selectedCount={selectedRows.length}
                supplyCategories={supplyCategories}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                onBulkApplyRequest={handleBulkApplyRequest}
                onClearSelection={() => dispatch(clearBomRowSelection("supplies"))}
            />

            {/* Table — flat catalog with bulk selection */}
            <div className="min-h-0 flex-1 overflow-auto bg-white">
                <table className="w-full border-collapse">
                        <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
                            <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                <th className="pl-4 inline-flex items-center px-2 py-2.5">
                                    <input
                                        ref={headerCheckboxRef}
                                        type="checkbox"
                                        checked={allFilteredSelected && filteredRows.length > 0}
                                        onChange={toggleSelectAllFiltered}
                                        className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-400 cursor-pointer accent-primary"
                                        aria-label="Select all visible rows"
                                    />
                                </th>
                                <th className="px-4 py-2.5 text-left">SKU</th>
                                <th className="px-4 py-2.5 text-left">Name</th>
                                <th className="px-4 py-2.5 text-left">Category</th>
                                <th className="px-4 py-2.5 text-left">Supplier</th>
                                <th className="px-4 py-2.5 text-right">Unit Cost</th>
                                <th className="px-4 py-2.5 text-left">Status</th>
                                <th className="px-4 py-2.5 text-left">Notes</th>
                                <th className="w-16 px-4 py-2.5" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredRows.map((item) => (
                                <tr
                                    key={item.id}
                                    className="group bg-white transition-colors hover:bg-gray-50/60"
                                >
                                    <td className="px-2 py-2.5 align-bottom pl-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(getSupplyRowId(item))}
                                            onChange={() =>
                                                dispatch(
                                                    toggleBomRowSelection({
                                                        tab: "supplies",
                                                        id: getSupplyRowId(item),
                                                    })
                                                )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            className="h-3.5 w-3.5 rounded border-gray-300 text-gray-900 focus:ring-gray-400 cursor-pointer accent-primary"
                                            aria-label={`Select ${item.sku || "row"}`}
                                        />
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <SupplySku sku={item.sku} />
                                    </td>
                                    <td className="max-w-[220px] px-4 py-2.5 text-sm font-medium text-gray-900">
                                        <span className="block truncate">{item.canonicalName || item.name}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-sm text-gray-500">
                                        {item.category || "—"}
                                    </td>
                                    <td className="max-w-[160px] px-4 py-2.5 text-sm text-gray-500">
                                        <span className="block truncate">{item.supplier || "—"}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right text-sm font-semibold tabular-nums text-gray-900">
                                        {item.unitCost == null ? "—" : `$${Number(item.unitCost).toFixed(2)}`}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <SupplyStatusBadge status={item.status} statusCode={item.status_code} />
                                    </td>
                                    <td className="max-w-[160px] px-4 py-2.5 text-sm text-gray-500">
                                        <span className="block truncate">{item.notes || "—"}</span>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEdit(item);
                                                }}
                                                className="text-gray-400 hover:text-gray-900"
                                                title="Edit"
                                            >
                                                <PencilSquareIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => handleDelete(item, e)}
                                                className="text-gray-400 hover:text-red-400"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredRows.length === 0 && (
                        <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-300 bg-gray-50">
                            <ArchiveBoxIcon className="h-10 w-10 opacity-40" />
                            <p className="text-sm text-gray-400">No supply items match your filters</p>
                        </div>
                    )}
            </div>

            <SupplyDrawer
                isOpen={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setEditingItem(null);
                }}
                initialValue={editingItem}
                supplyCategories={supplyCategories}
                onCreateCategory={async (name) => {
                    const created = await dispatch(createSupplierCategory({ name })).unwrap();
                    await dispatch(fetchSuppliesBomUi()).unwrap();
                    return created;
                }}
                onDeleteCategory={async (id) => {
                    const deleted = await dispatch(deleteSupplierCategory({ id })).unwrap();
                    await dispatch(fetchSuppliesBomUi()).unwrap();
                    return deleted;
                }}
                categoryMutationBusy={categoryMutationBusy}
                vendorOptions={vendorOptions}
                vendorSelectLoading={vendorSelectLoading}
                isSaving={isSavingSupply}
                onSave={async (payload) => {
                    try {
                        await dispatch(upsertBomSupply(payload)).unwrap();
                        await dispatch(fetchSuppliesBomUi()).unwrap();
                        toast.success(
                            editingItem ? `${editingItem.sku} updated` : "Supply added",
                            { position: "bottom-right" }
                        );
                        setDrawerOpen(false);
                        setEditingItem(null);
                    } catch (error) {
                        const message = typeof error === "string" ? error : error?.message || "Failed to save supply";
                        toast.error(message, { position: "bottom-right" });
                    }
                }}
            />
            <ConfrimModal
                isOpen={deleteConfirmOpen}
                isLoading={loadingKeys.includes(BomUiLoadingKeys.deleteSupply)}
                title={`Are you sure you want to remove ${deleteTarget?.sku || "this supply"} from supplies?`}
                confirmButtonTitle="Delete"
                submitClick={confirmDelete}
                cancelClick={() => {
                    if (!loadingKeys.includes(BomUiLoadingKeys.deleteSupply)) {
                        setDeleteConfirmOpen(false);
                        setDeleteTarget(null);
                    }
                }}
                size="max-w-lg"
            />
            <ConfrimModal
                isOpen={Boolean(bulkConfirm)}
                isLoading={bulkRunning}
                title={bulkConfirmTitle()}
                confirmButtonTitle={bulkConfirm?.kind === "delete" ? "Remove" : "Apply"}
                submitClick={confirmBulkApply}
                cancelClick={() => {
                    if (!bulkRunning) setBulkConfirm(null);
                }}
                size="max-w-lg"
            />
        </div>
    );
};

export default SuppliesBomPanel;
